import { AxiosError } from 'axios';
import type { ReactNode } from 'react';
import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { useFetchCurrentUser } from '@MP/shared/api/hooks/auth';
import useLogout from '@MP/shared/hooks/useLogout';
import WebTokenManager from '@MP/shared/utils/WebTokenManager';

interface CheckAuthProps {
  children: ReactNode;
}

/**
 * 인증 체크 컴포넌트
 * 토큰이 없거나 만료되었으면 로그인 페이지로 리다이렉트합니다.
 */
const CheckAuth = ({ children }: CheckAuthProps) => {
  const { logout } = useLogout();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirectUrl');
  const { data: currentUser, isError, error } = useFetchCurrentUser();

  React.useEffect(() => {
    const forceLogout = searchParams.get('forceLogout');
    if (forceLogout) {
      logout(redirectUrl);
    }
  }, [logout, searchParams, redirectUrl]);

  if (!WebTokenManager.hasToken()) {
    return <Navigate to={'/login'} replace />;
  }

  // 토큰은 있지만 만료된 경우(401)에 대한 방어적 처리: 전역 QueryCache 핸들러가
  // 동작하지 않을 때에도 빈 화면에서 멈추지 않도록 명시적으로 로그인으로 보낸다.
  if (isError && (error as AxiosError)?.response?.status === 401) {
    const target = redirectUrl ?? window.location.href;
    return <Navigate to={`/login?redirectUrl=${target}`} replace />;
  }

  if (currentUser && currentUser.isSubscribeMarketingEvent === null) {
    const query = redirectUrl ? `?redirectUrl=${redirectUrl}` : `?redirectUrl=${window.location.href}`;
    return <Navigate to={`/re-verification${query}`} replace />;
  }

  if (!currentUser) {
    return null;
  }

  return <>{children}</>;
};

export { CheckAuth };
